import moment from "moment";
import { useCallback, useMemo } from "react";
import { MeetingsOverviewMeetingFragment } from "types/graphql-schema";

import { MeetingVideoConferenceImage } from "@apps/meeting-new/components/meeting/video-conference-button";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import MotionDiv from "@components/motion/motion-div";
import { classNames } from "@helpers/css";
import { getUrl, isTailwindLgActive } from "@helpers/helpers";

const OverviewMeeting = ({
  meeting,
  onClickMeetingLink,
  compact: _compact = false,
  isPast = false,
  now,
}: {
  meeting: MeetingsOverviewMeetingFragment;
  hideEditingButtons?: boolean;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
  compact?: boolean;
  isPast?: boolean;
  now: string;
}) => {
  const link = useLink();

  const nowTime = moment(now).toDate().getTime();
  const tsStart = meeting.startDatetime
    ? new Date(meeting.startDatetime).getTime()
    : null;
  const tsEnd = meeting.endDatetime
    ? new Date(meeting.endDatetime).getTime()
    : null;
  const isAboutToStart =
    tsStart && tsEnd
      ? nowTime > tsStart - 6 * 60 * 1000 && nowTime < tsEnd
      : false;

  const meetingUrl = useMemo(
    () =>
      getUrl({
        meetingGroupId: meeting.meetingGroupId,
        meetingId: meeting.id,
      }),
    [meeting],
  );

  const handleGoToMeeting = useCallback(() => {
    link.redirect(meetingUrl);
  }, [link, meetingUrl]);

  const handleClickMeetingLink = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
      if (onClickMeetingLink && meeting.meetingGroup?.id) {
        if (isTailwindLgActive()) {
          e.stopPropagation();
          e.preventDefault();
          onClickMeetingLink(meeting.id, meeting.meetingGroup.id);
        } else {
          handleGoToMeeting();
        }
      }
    },
    [
      meeting.id,
      onClickMeetingLink,
      meeting.meetingGroup?.id,
      handleGoToMeeting,
    ],
  );

  return (
    <>
      <MotionDiv
        className={classNames(
          "text-sm rounded py-1 hover:bg-nav-item-active",
          isPast ? "text-gray-400" : "text-gray-800",
          window.location.pathname === meetingUrl && "bg-nav-item-active",
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        role="button"
        onClick={onClickMeetingLink ? handleClickMeetingLink : undefined}
      >
        <AppLink
          className="flex items-center gap-2 w-full min-w-0 px-2"
          to={meetingUrl}
          onClick={handleClickMeetingLink}
        >
          <span className="flex-1 min-w-0 truncate">{meeting.title}</span>
          <span className="shrink-0 flex items-center gap-1 w-[80px] justify-end">
            {meeting.videoConferenceUrl && isAboutToStart && (
              <button
                className="shrink-0 rounded p-0.5 hover:bg-black/5"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(meeting.videoConferenceUrl!, "_blank");
                }}
              >
                <MeetingVideoConferenceImage meeting={meeting} />
              </button>
            )}
            <span
              className={classNames(
                "text-xs w-[52px] text-right",
                isPast ? "text-gray-400" : "text-gray-500",
              )}
            >
              {`${moment(meeting.startDatetime)
                .format("h:mma")
                .replace(":00", "")}`}
            </span>
          </span>
        </AppLink>
      </MotionDiv>
    </>
  );
};

export default OverviewMeeting;
